// src/components/Navbar.js
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Avatar,
  Menu,
  MenuItem,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  Box,
  Tooltip,
  Stack,
  Skeleton,
  Chip,
  Badge,
  LinearProgress,
  CircularProgress,
  Alert,
  ListItemButton,
  ListSubheader,
  FormControl,
  Select,
} from '@mui/material';
import { motion } from 'framer-motion';
import MenuIcon from '@mui/icons-material/Menu';
import MenuOpenIcon from '@mui/icons-material/MenuOpen';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import HomeIcon from '@mui/icons-material/Home';
import CasinoIcon from '@mui/icons-material/Casino';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ShareIcon from '@mui/icons-material/Share';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import GppBadIcon from '@mui/icons-material/GppBad';
import SportsSoccerIcon from '@mui/icons-material/SportsSoccer';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PublicIcon from '@mui/icons-material/Public';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import TwitterIcon from '@mui/icons-material/Twitter';
import TelegramIcon from '@mui/icons-material/Telegram';
import GitHubIcon from '@mui/icons-material/GitHub';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import { Link, useLocation } from 'react-router-dom';
import { WalletButton, WalletContext, CHAIN_REGISTRY, switchToChain } from './WalletConnect';
import { toast } from 'react-toastify';
import LocalLogo from '../assets/chainplaybet_dice.png';
import theme from '../theme';

/** Theme variable shims (all values are governed by theme.js CSS vars) */
const T = {
  colors: {
    background: 'var(--color-background, #0b0614)',
    surface: 'var(--color-surface, #12121a)',
    surfaceAlt: 'var(--color-surfaceAlt, #0f0f17)',
    card: 'var(--color-card, #151522)',
    text: 'var(--color-text, #ffffff)',
    subText: 'var(--color-subText, rgba(255,255,255,0.72))',
    primary: 'var(--color-primary, #7C4DFF)',
    primaryHover: 'var(--color-primaryHover, #9E7BFF)',
    accent: 'var(--color-accent, #FF63DE)',
    accentHover: 'var(--color-accentHover, #FFA1EE)',
    success: 'var(--color-success, #21D07A)',
    warning: 'var(--color-warning, #FFC107)',
    danger: 'var(--color-danger, #FF6E40)',
    border: 'var(--color-border, rgba(255,255,255,0.12))',
  },
  radii: {
    card: 'var(--radius-card)',
    chip: 'var(--radius-chip)',
    button: 'var(--radius-button)',
    pill: 'var(--radius-pill)',
  },
  z: { bar: 1300, mobileCta: 1299 },
};

// --- URL helpers to sanitize env/CDN values ---
const normalizeUrl = (u) => {
  if (!u) return '';
  let s = String(u).trim();
  if (s.startsWith('ttps://')) s = 'h' + s;
  if (s.startsWith('ttp://'))  s = 'h' + s;
  if (s.startsWith('//'))      s = 'https:' + s;
  return s;
};
const isHttpUrl = (u) => {
  try { const x = new URL(u); return x.protocol === 'http:' || x.protocol === 'https:'; } catch { return false; }
};

const safeWindow = () => { try { return typeof window !== 'undefined' ? window : null; } catch { return null; } };
const safeDocument = () => { try { return typeof document !== 'undefined' ? document : null; } catch { return null; } };
const prefersReducedMotion = () =>
  typeof window !== 'undefined' &&
  window.matchMedia
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false;

const shortAddr = (a) => (a ? `${a.slice(0, 6)}…${a.slice(-4)}` : '');
const fmt = (v, d = 4) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(d) : (0).toFixed(d);
};

const CDN_BASE =
  process.env.REACT_APP_CDN_BASE ||
  'https://pub-fe155522e76e45dab08986eda5f3b6e1.r2.dev';
const ENV_LOGO = (process.env.REACT_APP_LOGO_URL || '/bluedice.png').trim();
const CDN_LOGO_PRIMARY = `${CDN_BASE}/images/chainplaybet_dice.png`;
const CDN_LOGO_FALLBACK = `${CDN_BASE}/images/chainplay_bet_simple.png`;

/** Nav model (reads VIP colors from theme.vip) */
const onchainNav = {
  to: '/token',
  label: 'On-chain',
  icon: <MonetizationOnIcon />,
  subItems: [
    { to: '/token', label: 'Token' },
    { to: '/nft', label: 'NFT' },
    { to: '/staking', label: 'Staking' },
    { to: '/dao', label: 'DAO' },
  ],
};

/** Sports (own dropdown; links disabled while building) */
const sportsNav = {
  to: '/sports',
  label: 'Sports',
  icon: <SportsSoccerIcon />,
  subItems: [
    { to: '/sports/live',     label: 'Live Now',  disabled: true, soon: true },
    { to: '/sports/upcoming', label: 'Upcoming',  disabled: true, soon: true },
    { to: '/sports/nba',      label: 'NBA',       disabled: true, soon: true },
    { to: '/sports/nfl',      label: 'NFL',       disabled: true, soon: true },
    { to: '/sports/soccer',   label: 'Soccer',    disabled: true, soon: true },
  ],
};

/** Prediction Markets (separate dropdown; links disabled for now) */
const marketsNav = {
  to: '/markets',
  label: 'Prediction',
  icon: <TrendingUpIcon />,
  subItems: [
    { to: '/markets/crypto',   label: 'Crypto',      disabled: true, soon: true },
    { to: '/markets/politics', label: 'Politics',    disabled: true, soon: true },
    { to: '/markets/esports',  label: 'Esports',     disabled: true, soon: true },
    { to: '/markets/pop',      label: 'Pop Culture', disabled: true, soon: true },
  ],
};

/** Primary nav (About hidden for now) */
const navLinks = [
  { to: '/', label: 'Home', icon: <HomeIcon /> },
  { to: '/join', label: 'Join Beta', icon: <RocketLaunchIcon />, cta: true },
  {
    to: '/games',
    label: 'Games',
    icon: <CasinoIcon />,
    subItems: [
      // PvP group
      { to: '/games/honeypot', label: 'Honey Pot', hot: true, group: 'pvp' },
      { to: '/games/bossfight', label: 'Boss Fight', group: 'pvp' },
      { to: '/games/ticketsnipe', label: 'Ticket Snipe', group: 'pvp' },

      // Other games
      { to: '/games/mines', label: 'Mines', hot: true, disabled: false, dev: true },
      { to: '/games/coinflip', label: 'Coinflip', disabled: false, dev: true },
      { to: '/games/dice', label: 'Dice', hot: true, disabled: false, dev: true },
      { to: '/games/plinko', label: 'Plinko', hot: true, disabled: false, dev: true },
      { to: '/games/slots', label: 'Slots', hot: true, disabled: false, dev: true },
      { to: '/games/crash', label: 'Crash', new: true, disabled: false, dev: true },
      { to: '/games/hilo', label: 'Hi-Lo', disabled: false, dev: false },
      { to: '/games/keno', label: 'Keno', disabled: false, dev: false },
      { to: '/games/roulette', label: 'Roulette', disabled: false, dev: false },
      // Blackjack: not yet implemented, hidden from nav
      { to: '/games/coins', label: 'Coins', disabled: false, dev: true },
    ],
  },
  // sportsNav,    // Hidden: all items disabled/coming-soon — re-enable when live
  // marketsNav,   // Hidden: all items disabled/coming-soon — re-enable when live
  onchainNav,
];

// (About dropdown intentionally omitted for now)

const vipLevels = [
  { level: 'Titan',  icon: '', color: theme.vip.palette.shrimp,  need: 0,   rake: 0  },
  { level: 'Flux',   icon: '', color: theme.vip.palette.fish,    need: 20,  rake: 2  },
  { level: 'Void',   icon: '', color: theme.vip.palette.dolphin, need: 50,  rake: 5  },
  { level: 'Mythic', icon: '', color: theme.vip.palette.whale,   need: 100, rake: 10 },
];

// Desktop sidebar widths
const NAV_WIDTH = 280;   // expanded categories width
const COLLAPSED_WIDTH = 72;   // collapsed icon-only width
const TOPBAR_HEIGHT = 56; // thin header height

// Initialize topbar CSS var early
try {
  if (typeof document !== 'undefined') {
    const de = document.documentElement;
    if (!de.style.getPropertyValue('--topbar-height')) {
      de.style.setProperty('--topbar-height', `${TOPBAR_HEIGHT}px`);
    }
  }
} catch {}

/** VIP rank icons (tintable masks from /public) */
const levelIconSrc = (level) => {
  const name = String(level || '').toLowerCase();
  if (name.includes('mythic')) return '/crystalfour.svg';
  if (name.includes('void'))   return '/crystalthree.svg';
  if (name.includes('flux'))   return '/crystaltwo.svg';
  if (name.includes('titan'))  return '/crystalone.svg';
  return '/crystalone.svg';
};

/** Mini jackpot tag */
const JackpotMini = ({ value }) => (
  <Stack
    direction="row"
    spacing={1}
    alignItems="center"
    sx={{
      px: 1,
      py: 0.25,
      borderRadius: T.radii.card,
      bgcolor: 'rgba(255,255,255,0.06)',
      border: `1px solid ${T.colors.border}`,
    }}
  >
    <EmojiEventsIcon sx={{ fontSize: 18, color: T.colors.accent }} />
    <Typography
      sx={{
        fontWeight: 900,
        color: T.colors.text,
        fontSize: 13,
        letterSpacing: 0.3,
        fontVariantNumeric: 'tabular-nums',
        whiteSpace: 'nowrap',
      }}
    >
      {value.toLocaleString()} CPT
    </Typography>
  </Stack>
);

/** VIP badge + tooltip with progress */
const VIPBadge = ({ totalCPT }) => {
  const index = useMemo(() => {
    let i = 0;
    for (let k = 1; k < vipLevels.length; k++) {
      if (totalCPT >= vipLevels[k].need) i = k;
      else break;
    }
    return i;
  }, [totalCPT]);
  const current = vipLevels[index];
  const next = vipLevels[index + 1];
  const baseNeed = current.need;
  const nextNeed = next?.need ?? baseNeed;
  const raw =
    nextNeed > baseNeed ? ((totalCPT - baseNeed) / (nextNeed - baseNeed)) * 100 : 100;
  const progress = Math.max(0, Math.min(100, raw));
  return (
    <Tooltip
      title={
        <Box sx={{ p: 1 }}>
          <Typography sx={{ fontWeight: 900, mb: 0.5, color: current.color }}>
            VIP {current.level}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 8,
              borderRadius: T.radii.card,
              '& .MuiLinearProgress-bar': { backgroundColor: current.color, borderRadius: T.radii.card },
            }}
          />
          <Typography sx={{ mt: 0.5, fontSize: 12, color: T.colors.text }}>
            {next
              ? `${Math.max(0, (nextNeed - totalCPT)).toFixed(1)} CPT to ${next.level}`
              : 'Top tier achieved'}
          </Typography>
        </Box>
      }
      arrow
      placement="bottom"
    >
      <Chip
        label={
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Box
              aria-hidden
              sx={{
                width: 16,
                height: 16,
                backgroundColor: current.color,
                WebkitMask: `url(${levelIconSrc(current.level)}) no-repeat center / contain`,
                mask: `url(${levelIconSrc(current.level)}) no-repeat center / contain`,
                display: 'inline-block',
                mr: 0.25,
                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.22))',
              }}
            />
            <Box component="span">VIP {current.level}</Box>
          </Stack>
        }
        sx={{
          color: '#000',
          bgcolor: current.color,
          borderRadius: T.radii.chip,
          height: 28,
          px: 0,
          '& .MuiChip-label': {
            display: 'flex',
            alignItems: 'center',
            fontWeight: 900,
            fontSize: 12,
            letterSpacing: 0.4,
            px: 0.75,
          },
        }}
      />
    </Tooltip>
  );
};

/** Avatar with circular VIP ring */
const VIPAvatar = ({ address, progress, color, hasRewards, onClick }) => {
  const size = 36;
  return (
    <Badge color="warning" variant="dot" overlap="circular" invisible={!hasRewards}>
      <Box sx={{ position: 'relative', width: size, height: size }}>
        {address && progress > 0 && (
          <CircularProgress
            variant="determinate"
            value={progress}
            size={size + 6}
            thickness={4}
            sx={{ position: 'absolute', top: -3, left: -3, color }}
          />
        )}
        <Avatar
          onClick={onClick}
          sx={{
            bgcolor: T.colors.accent,
            color: T.colors.background,
            cursor: 'pointer',
            width: size,
            height: size,
            fontWeight: 800,
          }}
        >
          {address ? address.slice(2, 4).toUpperCase() : <AccountCircleIcon fontSize="small" />}
        </Avatar>
      </Box>
    </Badge>
  );
};

const useLogoSrc = () => {
  const sources = useMemo(() => {
    const raw = [ENV_LOGO, CDN_LOGO_PRIMARY, CDN_LOGO_FALLBACK, LocalLogo].filter(Boolean);
    const normalized = raw.map((u) => (u === LocalLogo ? u : normalizeUrl(u)));
    const safe = normalized.filter(
      (u) => u === LocalLogo || (typeof u === 'string' && (u.startsWith('/') || isHttpUrl(u)))
    );
    return [...new Set(safe)];
  }, []);

  const [idx, setIdx] = useState(0);
  const src = sources[Math.min(idx, sources.length - 1)];

  useEffect(() => {
    try {
      const d = safeDocument(); if (!d) return;
      const srcUrl = (() => { try { return new URL(src, d.location?.href || 'https://chainplay.bet'); } catch { return null; } })();
      if (!srcUrl || !(srcUrl.protocol === 'http:' || srcUrl.protocol === 'https:')) return;
      const host = srcUrl.origin;
      if (!d.querySelector(`link[rel="preconnect"][href="${host}"]`)) {
        const linkPreconnect = d.createElement('link');
        linkPreconnect.rel = 'preconnect';
        linkPreconnect.href = host;
        d.head.appendChild(linkPreconnect);
      }
    } catch {}
  }, [src]);

  useEffect(() => {
    const d = safeDocument(); if (!d) return;
    const created = sources.slice(0, 2).map((url) => {
      if (!url || url === LocalLogo) return null;
      if (!isHttpUrl(url)) return null;
      if (d.querySelector(`link[rel="preload"][as="image"][href="${url}"]`)) return null;
      const link = d.createElement('link');
      link.rel = 'preload';
      link.as = 'image';
      link.href = url;
      d.head.appendChild(link);
      return link;
    });
    return () => created.forEach((l) => l && l.remove());
  }, [sources]);

  const onError = () => {
    if (idx < sources.length - 1) setIdx(idx + 1);
  };

  return { src, onError };
};

const Navbar = () => {
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('md'));
  const motionOff = prefersReducedMotion();
  const { balance, address, isLoading: isWalletLoading, error: walletError, chainId: ctxChainId } =
    useContext(WalletContext) || {};
  const location = useLocation();
  const { src: logoSrc, onError: onLogoError } = useLogoSrc();

  const safeBalance = useMemo(
    () => ({ eth: balance?.eth ?? '0', cpt: balance?.cpt ?? '0' }),
    [balance]
  );
  const totalCPT = address && safeBalance.cpt ? parseFloat(safeBalance.cpt) || 0 : 0;

  const [anchorProfile, setAnchorProfile] = useState(null);
  const [anchorChain, setAnchorChain] = useState(null);

  const openChain = useCallback((e) => setAnchorChain(e.currentTarget), []);
  const closeChain = useCallback(() => setAnchorChain(null), []);

  const chains = useMemo(
    () => Object.values(CHAIN_REGISTRY).sort((a, b) => a.chainId - b.chainId),
    []
  );
  const activeNetworkLabel = useMemo(() => {
    const current = chains.find((c) => c.chainId === ((ctxChainId || 8453))) || CHAIN_REGISTRY.base;
    return current.shortName || current.name;
  }, [chains, ctxChainId]);

  const doSwitchChain = useCallback(async (key) => {
    try {
      await switchToChain(key);
      toast.success(`Switched to ${CHAIN_REGISTRY[key].name}`);
    } catch (e) {
      toast.error(e?.message || 'Switch failed');
    } finally {
      closeChain();
    }
  }, [closeChain]);





  const [drawerOpen, setDrawerOpen] = useState(false);
  const toggleDrawer = useCallback(() => setDrawerOpen((o) => !o), []);
  const closeDrawer  = useCallback(() => setDrawerOpen(false), []);

  // Collapsible desktop sidebar
  const [collapsed, setCollapsed] = useState(() => {
    try { return localStorage.getItem('nav_collapsed') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try {
      localStorage.setItem('nav_collapsed', collapsed ? '1' : '0');
      const de = document.documentElement;
      const leftWidth = collapsed ? COLLAPSED_WIDTH : NAV_WIDTH;
      de.style.setProperty('--nav-left-width', `${leftWidth}px`);
      // Ensure topbar var is present
      if (!de.style.getPropertyValue('--topbar-height')) {
        de.style.setProperty('--topbar-height', `${TOPBAR_HEIGHT}px`);
      }
    } catch {}
  }, [collapsed]);
  useEffect(() => {
    // Initialize on mount in case root var missing
    try {
      const de = document.documentElement;
      if (!de.style.getPropertyValue('--nav-left-width')) {
        const leftWidth = collapsed ? COLLAPSED_WIDTH : NAV_WIDTH;
        de.style.setProperty('--nav-left-width', `${leftWidth}px`);
      }
      if (!de.style.getPropertyValue('--topbar-height')) {
        de.style.setProperty('--topbar-height', `${TOPBAR_HEIGHT}px`);
      }
    } catch {}
  }, [collapsed]);

  const [elevated, setElevated] = useState(false);
  const [jackpot, setJackpot] = useState(128420);
  const [rewardsBadge, setRewardsBadge] = useState(false);
  // recentGames sidebar chips removed (state no longer needed)



  const [chainId, setChainId] = useState(ctxChainId || null);
  useEffect(() => {
    const onScroll = () => setElevated(typeof window !== 'undefined' ? window.scrollY > 4 : false);
    onScroll();
    if (typeof window !== 'undefined')
      window.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      if (typeof window !== 'undefined') window.removeEventListener('scroll', onScroll);
    };
  }, []);

  useEffect(() => {
    const id = setInterval(() => setJackpot((j) => Math.round(j + 15 + Math.random() * 60)), 3000);
    return () => clearInterval(id);
  }, []);

  const [balanceKey, setBalanceKey] = useState(`${safeBalance.eth}-${safeBalance.cpt}`);
  const [debouncedBalanceKey, setDebouncedBalanceKey] = useState(balanceKey);
  useEffect(() => {
    const key = `${safeBalance.eth}-${safeBalance.cpt}`;
    if (key !== balanceKey) setBalanceKey(key);
  }, [safeBalance, balanceKey]);
  useEffect(() => {
    const t = setTimeout(() => setDebouncedBalanceKey(balanceKey), 220);
    return () => clearTimeout(t);
  }, [balanceKey]);

  useEffect(() => {
    try {
      if (typeof window !== 'undefined') {
        const rawQ = localStorage.getItem('cpt_quests');
        const rawP = localStorage.getItem('cpt_promos');
        const rawR = localStorage.getItem('cpt_rakeback');
        const q = rawQ ? JSON.parse(rawQ) : {};
        const p = rawP ? JSON.parse(rawP) : {};
        const r = rawR ? JSON.parse(rawR) : {};
        const hasUnclaimedQ = Object.values(q).some((x) => x && x.done && !x.claimed);
        const hasUnclaimedP = Object.values(p).some((x) => x && x.unclaimed);
        const hasUnclaimedR = !!r?.unclaimed;
        setRewardsBadge(Boolean(hasUnclaimedQ || hasUnclaimedP || hasUnclaimedR));
      }
    } catch {
      setRewardsBadge(false);
    }
  }, []);

  useEffect(() => {
    setChainId((prev) => ctxChainId ?? prev);
    if (!ctxChainId && typeof window !== 'undefined' && window.ethereum?.chainId) {
      const idHex = window.ethereum.chainId;
      const parsed = Number.isInteger(idHex) ? Number(idHex) : parseInt(String(idHex), 16);
      if (!Number.isNaN(parsed)) setChainId(parsed);
    }
  }, [ctxChainId]);

  useEffect(() => {
    setDrawerOpen(false);
    setAnchorProfile(null);
    setAnchorChain(null);
  }, [location.pathname]);

  const isActive = useCallback(
    (path) => {
      if (path === '/') return location.pathname === '/';
      return location.pathname.startsWith(path);
    },
    [location.pathname]
  );

  const copyAddress = useCallback(() => {
    if (!address) return;
    try {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(address);
        toast.success('Address copied');
      } else {
        throw new Error('no_clipboard');
      }
    } catch {
      try { safeWindow()?.prompt?.('Copy address', address); } catch {}
      toast.info('Copy failed — prompt shown');
    }
  }, [address]);

  const buildCanonicalReferral = useCallback((addr) => {
    if (!addr) return '';
    try {
      const base =
        process.env.PUBLIC_URL || (typeof window !== 'undefined' ? `${window.location.origin}` : '');
      const url = new URL(base);
      url.search = '';
      url.hash = '';
      url.pathname = '/';
      url.searchParams.set('ref', addr);
      return url.toString();
    } catch {
      return '';
    }
  }, []);

  const copyReferral = useCallback(() => {
    if (!address) return;
    const link = buildCanonicalReferral(address);
    if (!link) return;
    try {
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard.writeText(link);
        toast.success('Referral link copied');
      } else {
        throw new Error('no_clipboard');
      }
    } catch {
      try { safeWindow()?.prompt?.('Copy referral link', link); } catch {}
      toast.info('Copy failed — prompt shown');
    }
  }, [address, buildCanonicalReferral]);

  const BASE_CHAIN_ID = 8453;
  const CAMP_MAINNET_CHAIN_ID = 484;
  const CAMP_TESTNET_CHAIN_ID = 325000;
  const isBase = chainId === BASE_CHAIN_ID;
  const isCamp = chainId === CAMP_MAINNET_CHAIN_ID || chainId === CAMP_TESTNET_CHAIN_ID;
  const wrongNetwork = !!chainId && !isBase && !isCamp;
  const campLimited = isCamp; // Camp = join-only mode (no contract interactions)

  const attemptSwitchNetwork = useCallback(async () => {
    try {
      await switchToChain('base');
      setChainId(8453);
      toast.success('Switched to Base');
    } catch (err) {
      toast.error(err?.message || 'Unable to switch network');
    }
  }, []);

  // Camp join-only helpers
  const campJoinToast = useCallback(() => {
    toast.info('Camp network: join-only mode. Switch to Base to play.');
  }, []);
  const campNavGuard = useCallback(
    (e) => {
      if (!campLimited) return;
      try {
        const w = safeWindow();
        const t = e.target;
        const anchor = t?.closest ? t.closest('a') : null;
        if (!anchor) return;
        if (anchor.getAttribute('target') === '_blank') return;

        const href = anchor.getAttribute('href') || '';
        let url;
        try {
          url = new URL(href, w?.location?.href || 'https://chainplay.bet');
        } catch {
          url = null;
        }
        if (!url) return;
        const isExternal = url.origin !== (w?.location?.origin || '');
        if (isExternal) return;

        const path = url.pathname || '/';
        const allowed = path === '/' || path.startsWith('/join') || path.startsWith('/terms') || path.startsWith('/privacy');
        if (!allowed) {
          e.preventDefault();
          e.stopPropagation();
          campJoinToast();
        }
      } catch {}
    },
    [campLimited, campJoinToast]
  );

  // Keyboard: Ctrl/Cmd+B toggles sidebar; Ctrl/Cmd+J toggles chat
  useEffect(() => {
    const onKey = (e) => {
      const mod = e.ctrlKey || e.metaKey;
      if (mod && (e.key === 'b' || e.key === 'B')) {
        e.preventDefault();
        setCollapsed((c) => !c);
        return;
      }
      if (mod && (e.key === 'j' || e.key === 'J')) {
        e.preventDefault();
        try { window.dispatchEvent(new Event('toggle_chat_dock')); } catch {}
        return;
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Sidebar: keyboard navigation and auto-scroll to active
  const sideScrollRef = useRef(null);
  const sideKeyHandler = useCallback((e) => {
    const container = sideScrollRef.current;
    if (!container) return;
    const key = e.key;
    if (key !== 'ArrowDown' && key !== 'ArrowUp' && key !== 'Home' && key !== 'End') return;

    const items = Array.from(container.querySelectorAll('a.MuiButtonBase-root, div.MuiButtonBase-root'));
    if (!items.length) return;

    const activeEl = document.activeElement;
    let idx = items.indexOf(activeEl);
    if (key === 'ArrowDown') {
      e.preventDefault();
      items[(idx + 1 + items.length) % items.length].focus();
    } else if (key === 'ArrowUp') {
      e.preventDefault();
      items[(idx - 1 + items.length) % items.length].focus();
    } else if (key === 'Home') {
      e.preventDefault();
      items[0].focus();
    } else if (key === 'End') {
      e.preventDefault();
      items[items.length - 1].focus();
    }
  }, []);

  useEffect(() => {
    try {
      const c = sideScrollRef.current; if (!c) return;
      const active = c.querySelector('.MuiListItemButton-root.Mui-selected');
      if (active && typeof active.scrollIntoView === 'function') {
        active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    } catch {}
  }, [location.pathname]);


  // DesktopLinks removed — desktop navigation is handled via the left sidebar

  /** Mobile drawer content */
  const DrawerContent = (
    <Box onClickCapture={campNavGuard} sx={{ width: '100%', p: 2, bgcolor: T.colors.background, height: '100%', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 1.25 }}>
        <Link to="/" onClick={closeDrawer} aria-label="Home" style={{ display: 'inline-flex' }}>
          <img
            src={logoSrc}
            onError={onLogoError}
            alt="ChainPlay logo"
            style={{ height: 32, display: 'block', objectFit: 'contain' }}
            loading="eager"
            decoding="async"
          />
        </Link>
      </Box>

      {wrongNetwork && (
        <Alert
          severity="warning"
          icon={<GppBadIcon fontSize="small" />}
          action={<Button color="inherit" size="small" onClick={attemptSwitchNetwork}>Fix</Button>}
          sx={{ mb: 1, borderRadius: T.radii.card }}
        >
          Wrong network — switch to Base.
        </Alert>
      )}
      {campLimited && (
        <Alert
          severity="info"
          action={<Button color="inherit" size="small" onClick={attemptSwitchNetwork}>Switch to Base</Button>}
          sx={{ mb: 1, borderRadius: T.radii.card }}
        >
          Camp network detected — Join page only. Switch to Base to play.
        </Alert>
      )}

      <Divider sx={{ my: 1.5 }} />

      {/* Mobile: Network selector */}
      <Box role="group" aria-label="Network" sx={{ mb: 1.25 }}>
        <Typography sx={{ color: T.colors.subText, fontWeight: 800, fontSize: 12, mb: 0.75, textTransform: 'uppercase', letterSpacing: 0.6 }}>
          Network
        </Typography>
        <FormControl fullWidth size="small" variant="outlined">
          <Select
            displayEmpty
            value={String(ctxChainId || 8453)}
            renderValue={() => activeNetworkLabel}
            sx={{
              height: 36,
              borderRadius: T.radii.button,
              color: T.colors.text,
              bgcolor: 'rgba(255,255,255,0.06)',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: T.colors.border },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: T.colors.primary },
              '& .MuiSelect-icon': { color: T.colors.subText },
            }}
            onChange={(e) => {
              const val = Number(e.target.value);
              const entry = Object.entries(CHAIN_REGISTRY).find(([, v]) => v.chainId === val);
              const key = entry ? entry[0] : 'base';
              doSwitchChain(key);
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: T.colors.card,
                  backgroundImage: 'none',
                  borderRadius: T.radii.card,
                  border: `1px solid ${T.colors.border}`,
                  boxShadow: '0 12px 32px rgba(0,0,0,0.45)',
                }
              }
            }}
          >
            {chains.map((c) => {
              const disabled = !!c.comingSoon && c.key !== 'camp';
              const value = String(c.chainId);
              const isCurrent = (ctxChainId || 8453) === c.chainId;
              return (
                <MenuItem
                  key={`m-chain-${c.chainId}`}
                  value={value}
                  disabled={disabled}
                  sx={{
                    color: T.colors.text,
                    opacity: disabled ? 0.55 : 1,
                    '&:hover': { bgcolor: T.colors.surfaceAlt, color: disabled ? T.colors.subText : T.colors.accent },
                    display: 'flex',
                    justifyContent: 'space-between',
                  }}
                >
                  <span>{c.shortName || c.name}</span>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    {isCurrent && <CheckRoundedIcon sx={{ fontSize: 16, color: T.colors.primary }} />}
                    {disabled && <Chip size="small" label="Soon" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: T.colors.subText, borderRadius: T.radii.chip }} />}
                  </Stack>
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      </Box>

      <List sx={{ flex: 1 }}>
        {navLinks.map((link) => (
          <Box key={link.label}>
            <motion.div whileHover={motionOff ? undefined : { scale: 1.02 }}>
              <ListItem disablePadding sx={{ borderRadius: T.radii.card }}>
                <ListItemButton
                  component={Link}
                  to={link.to}
                  onClick={closeDrawer}
                  aria-current={isActive(link.to) ? 'page' : undefined}
                  sx={{
                    borderRadius: T.radii.card,
                    ...(link.cta
                      ? {
                          background: 'linear-gradient(90deg, rgba(255,99,222,0.22), rgba(124,77,255,0.16))',
                          border: `1px solid ${T.colors.border}`,
                          boxShadow: '0 10px 26px rgba(124,77,255,0.28)',
                        }
                      : {}),
                  }}
                >
                  <ListItemIcon sx={{ color: T.colors.accent, minWidth: 36 }}>{link.icon}</ListItemIcon>
                  <ListItemText
                    primary={
                      link.cta ? (
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Box component="span">Join Beta</Box>
                          <Chip
                            size="small"
                            label="Beta"
                            sx={{ bgcolor: 'rgba(255,99,222,0.25)', color: T.colors.text, borderRadius: T.radii.chip, height: 20 }}
                          />
                        </Stack>
                      ) : (
                        link.label
                      )
                    }
                    primaryTypographyProps={{ color: T.colors.text, fontWeight: 800 }}
                  />
                </ListItemButton>
              </ListItem>
            </motion.div>

            {link.subItems && (
              <Box sx={{ pl: 5 }}>
                {link.label === 'Games' ? (
                  <>
                    {/* Featured: hot/new across all games (includes PvP) */}
                    {link.subItems.some((g) => g.hot || g.new) && (
                      <>
                        <Typography sx={{ color: T.colors.subText, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>
                          Featured
                        </Typography>
                        {link.subItems
                          .filter((g) => g.hot || g.new)
                          .map((s) => (
                            <ListItem key={`m-featured-${s.label}`} disablePadding sx={{ borderRadius: T.radii.card }}>
                              <ListItemButton
                                component={s.disabled ? 'div' : Link}
                                to={s.disabled ? undefined : s.to}
                                onClick={(e) => { if (s.disabled) { e.preventDefault?.(); toast.info(s.soon ? 'Coming soon' : 'Under development'); } else { closeDrawer(); } }}
                                aria-current={isActive(s.to) ? 'page' : undefined}
                                disabled={!!s.disabled}
                                sx={{ borderRadius: T.radii.card, py: 0.5 }}
                              >
                                <ListItemText
                                  primary={
                                    <Stack direction="row" spacing={0.5} alignItems="center">
                                      <Box component="span">{s.label}</Box>
                                      {s.group === 'pvp' && (
                                        <Chip size="small" label="PvP" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: T.colors.text, borderRadius: T.radii.chip, height: 20 }} />
                                      )}
                                      {s.new && <Chip size="small" label="New" sx={{ bgcolor: 'rgba(255,99,222,0.25)', color: T.colors.text, borderRadius: T.radii.chip }} />}
                                      {s.hot && <Chip size="small" label="Hot" sx={{ bgcolor: 'rgba(124,77,255,0.18)', color: T.colors.text, borderRadius: T.radii.chip }} />}
                                      {s.disabled && <Chip size="small" label={s.soon ? 'Soon' : 'Dev'} sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: T.colors.subText, borderRadius: T.radii.chip }} />}
                                    </Stack>
                                  }
                                  primaryTypographyProps={{ color: T.colors.subText, fontSize: 13 }}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        <Box sx={{ height: 6 }} />
                      </>
                    )}

                    {/* All Games (A–Z), Blackjack pinned last */}
                    <Typography sx={{ color: T.colors.subText, fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: 0.8, mb: 0.5 }}>
                      All Games (A–Z)
                    </Typography>
                    {link.subItems
                      .slice()
                      .sort((a, b) => {
                        const na = String(a.label || '').toLowerCase();
                        const nb = String(b.label || '').toLowerCase();
                        if (na === 'blackjack') return 1;
                        if (nb === 'blackjack') return -1;
                        return na.localeCompare(nb);
                      })
                      .map((s) => (
                        <ListItem key={`m-all-${s.label}`} disablePadding sx={{ borderRadius: T.radii.card }}>
                          <ListItemButton
                            component={s.disabled ? 'div' : Link}
                            to={s.disabled ? undefined : s.to}
                            onClick={(e) => { if (s.disabled) { e.preventDefault?.(); toast.info(s.soon ? 'Coming soon' : 'Under development'); } else { closeDrawer(); } }}
                            aria-current={isActive(s.to) ? 'page' : undefined}
                            disabled={!!s.disabled}
                            sx={{ borderRadius: T.radii.card, py: 0.5 }}
                          >
                            <ListItemText
                              primary={
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <Box component="span">{s.label}</Box>
                                  {s.group === 'pvp' && (
                                    <Chip size="small" label="PvP" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: T.colors.text, borderRadius: T.radii.chip, height: 20 }} />
                                  )}
                                  {s.new && <Chip size="small" label="New" sx={{ bgcolor: 'rgba(255,99,222,0.25)', color: T.colors.text, borderRadius: T.radii.chip }} />}
                                  {s.hot && <Chip size="small" label="Hot" sx={{ bgcolor: 'rgba(124,77,255,0.18)', color: T.colors.text, borderRadius: T.radii.chip }} />}
                                  {s.disabled && <Chip size="small" label={s.soon ? 'Soon' : 'Dev'} sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: T.colors.subText, borderRadius: T.radii.chip }} />}
                                </Stack>
                              }
                              primaryTypographyProps={{ color: T.colors.subText, fontSize: 13 }}
                            />
                          </ListItemButton>
                        </ListItem>
                      ))}
                  </>
                ) : (
                  link.subItems.map((s) => (
                    <ListItem key={s.label} disablePadding sx={{ borderRadius: T.radii.card }}>
                      <ListItemButton
                        component={s.disabled ? 'div' : Link}
                        to={s.disabled ? undefined : s.to}
                        onClick={(e) => { if (s.disabled) { e.preventDefault?.(); toast.info('Under development'); } else { closeDrawer(); } }}
                        aria-current={isActive(s.to) ? 'page' : undefined}
                        disabled={!!s.disabled}
                        sx={{ borderRadius: T.radii.card, py: 0.5 }}
                      >
                        <ListItemText primary={s.label} primaryTypographyProps={{ color: T.colors.subText, fontSize: 13 }} />
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          {s.new && <Chip size="small" label="New" sx={{ bgcolor: 'rgba(255,99,222,0.25)', color: T.colors.text, borderRadius: T.radii.chip }} />}
                          {s.hot && <Chip size="small" label="Hot" sx={{ bgcolor: 'rgba(124,77,255,0.18)', color: T.colors.text, borderRadius: T.radii.chip }} />}
                          {s.disabled && <Chip size="small" label={s.soon ? 'Soon' : 'Dev'} sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: T.colors.subText, borderRadius: T.radii.chip }} />}
                        </Stack>
                      </ListItemButton>
                    </ListItem>
                  ))
                )}
              </Box>
            )}
          </Box>
        ))}
      </List>

      <Divider sx={{ my: 1.5 }} />

      <Stack spacing={1.25} alignItems="center">
        <Stack direction="row" spacing={1} alignItems="center">
          <JackpotMini value={jackpot} />
          <VIPBadge totalCPT={totalCPT} />
        </Stack>
        {isWalletLoading ? (
          <Skeleton variant="text" width={220} height={22} sx={{ mx: 'auto', mb: 1 }} />
        ) : walletError ? (
          <Typography sx={{ color: T.colors.danger, fontWeight: 800, mb: 1 }}>Wallet Error</Typography>
        ) : (
          <motion.div key={debouncedBalanceKey} initial={{ opacity: 0.6 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
            <Typography sx={{ color: T.colors.accent, fontWeight: 900, mb: 1, letterSpacing: 0.4, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>
              ETH {fmt(safeBalance.eth, 4)} • CPT {fmt(safeBalance.cpt, 2)}
            </Typography>
          </motion.div>
        )}
        <WalletButton />
      </Stack>
    </Box>
  );


  // Desktop sidebar content (permanent)
  const SideBarContent = (
    <Box onClickCapture={campNavGuard} sx={{ width: collapsed ? COLLAPSED_WIDTH : NAV_WIDTH, height: '100%', display: 'flex', flexDirection: 'column', bgcolor: T.colors.background }}>
      {/* Sidebar header removed — hamburger moved to top AppBar */}

      {/* Optional warning + network selector (match mobile) */}
      {!collapsed && wrongNetwork && (
        <Alert
          severity="warning"
          icon={<GppBadIcon fontSize="small" />}
          action={<Button color="inherit" size="small" onClick={attemptSwitchNetwork}>Fix</Button>}
          sx={{ mx: 1.5, mb: 1, borderRadius: T.radii.card }}
        >
          Wrong network — switch to Base.
        </Alert>
      )}
      {!collapsed && campLimited && (
        <Alert
          severity="info"
          action={<Button color="inherit" size="small" onClick={attemptSwitchNetwork}>Switch to Base</Button>}
          sx={{ mx: 1.5, mb: 1, borderRadius: T.radii.card }}
        >
          Camp network detected — Join page only. Switch to Base to play.
        </Alert>
      )}
      <List sx={{ px: collapsed ? 0 : 1.25, mb: 0.5 }}>
        <ListItem disablePadding>
          <Tooltip title={collapsed ? `Network: ${activeNetworkLabel}` : ''} placement="right">
            <ListItemButton
              onClick={openChain}
              aria-expanded={Boolean(anchorChain) || undefined}
              aria-label={collapsed ? `Network: ${activeNetworkLabel}` : undefined}
              sx={{
                borderRadius: T.radii.card,
                justifyContent: collapsed ? 'center' : 'flex-start',
                px: collapsed ? 1 : 1.25,
                minHeight: 40,
                transition: 'background-color .14s ease, border-color .14s ease, transform .14s ease',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                '&:focus-visible': { outline: '2px solid rgba(124,77,255,0.45)', outlineOffset: 2 },
                '&:hover .MuiListItemIcon-root': { transform: 'translateX(2px)' },
              }}
            >
              <ListItemIcon sx={{ color: T.colors.accent, minWidth: collapsed ? 0 : 36, mr: collapsed ? 0 : 1, '& svg': { fontSize: 22 } }}>
                <Badge color="warning" variant="dot" overlap="circular" invisible={!wrongNetwork}>
                  <PublicIcon />
                </Badge>
              </ListItemIcon>
              {!collapsed && (
                <ListItemText
                  primary="Network"
                  secondary={activeNetworkLabel}
                  primaryTypographyProps={{ fontWeight: 800, color: T.colors.text }}
                  secondaryTypographyProps={{ color: T.colors.subText }}
                />
              )}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>

      <Divider sx={{ my: 0.5, opacity: 0.18 }} />

      {/* Nav: show grouped categories for better flow */}
      <List sx={{ flex: 1, px: collapsed ? 0 : 1.25, overflow: 'visible', py: 0.25 }}>
        {(() => {
          const homeLink = navLinks.find((l) => l.to === '/');
          const joinLink = navLinks.find((l) => l.cta);
          const gamesLink = navLinks.find((l) => l.label === 'Games');
          const groups = [
            { key: 'overview', title: 'Overview', items: [homeLink, { to: '/dashboard', label: 'Dashboard', icon: <SpaceDashboardIcon /> }].filter(Boolean) },
            { key: 'play', title: 'Play', items: [gamesLink].filter(Boolean) },
            { key: 'explore', title: 'Explore', items: [sportsNav, marketsNav] },
            { key: 'onchain', title: 'On-chain', items: [onchainNav] },
            { key: 'get-started', title: 'Get Started', items: [joinLink].filter(Boolean) },
          ];

          return groups.map((g) => (
            <Box key={`grp-${g.key}`} sx={{ mb: 0.5 }}>
              {!collapsed && (
                <ListSubheader
                  disableSticky
                  sx={{
                    color: T.colors.subText,
                    fontSize: 11,
                    fontWeight: 800,
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    px: 0.5,
                    mb: 0.25,
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    bgcolor: T.colors.background,
                    borderBottom: `1px solid ${T.colors.border}`,
                    pb: 0.25,
                  }}
                >
                  {g.title}
                </ListSubheader>
              )}

              {g.items.map((item) => {
                const hasSub = Array.isArray(item?.subItems) && item.subItems.length > 0;
                const isDashboard = item?.to === '/dashboard' || item?.label === 'Dashboard';
                const isCta = !!item?.cta;
                const topStatus = hasSub && item.subItems.every((s) => !!s.disabled) ? 'Soon' : null;
                const topSelected = isActive(item.to);
                const hasActiveChild = hasSub && item.subItems.some((s) => isActive(s.to));

                return (
                  <Box key={`side-item-${item?.label || item?.to || 'unknown'}`} sx={{ mb: 0.25 }}>
                    <Tooltip title={collapsed ? (item?.label || (isDashboard ? 'Dashboard' : '')) : ''} placement="right">
                      <ListItem disablePadding>
                        <ListItemButton
                          component={Link}
                          to={item.to}
                          selected={topSelected}
                          aria-current={topSelected ? 'page' : undefined}
                          aria-label={!collapsed ? undefined : (item.label || (isDashboard ? 'Dashboard' : ''))}
                          sx={{
                            borderRadius: T.radii.card,
                            mb: 0.25,
                            justifyContent: collapsed ? 'center' : 'flex-start',
                            px: collapsed ? 1 : 1.25,
                            minHeight: 40,
                            transition: 'background-color .14s ease, border-color .14s ease, transform .14s ease',
                            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                            '&:focus-visible': { outline: '2px solid rgba(124,77,255,0.45)', outlineOffset: 2 },
                            '&:hover .MuiListItemIcon-root': { transform: 'translateX(2px)' },
                            '&.Mui-selected': {
                              bgcolor: 'rgba(255,255,255,0.10)',
                              color: T.colors.accent,
                              borderLeft: collapsed ? 'none' : `3px solid ${T.colors.primary}`,
                            },
                            ...(isCta
                              ? {
                                  background: 'linear-gradient(90deg, rgba(255,99,222,0.20), rgba(124,77,255,0.14))',
                                  border: `1px solid ${T.colors.border}`,
                                  boxShadow: '0 8px 22px rgba(124,77,255,0.28)',
                                }
                              : {}),
                          }}
                        >
                          <ListItemIcon sx={{ color: T.colors.accent, minWidth: collapsed ? 0 : 36, mr: collapsed ? 0 : 1, '& svg': { fontSize: 22 }, transition: 'transform .14s ease' }}>
                            {isDashboard ? (
                              <Badge color="warning" variant="dot" overlap="circular" invisible={!rewardsBadge}>
                                <SpaceDashboardIcon />
                              </Badge>
                            ) : hasActiveChild ? (
                              <Badge color="primary" variant="dot" overlap="circular" invisible={topSelected}>
                                <Box component="span" sx={{ display: 'inline-flex' }}>{item.icon}</Box>
                              </Badge>
                            ) : (
                              item.icon
                            )}
                          </ListItemIcon>
                          {!collapsed && (
                            <ListItemText
                              primary={
                                isCta ? (
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Box component="span">Join Beta</Box>
                                    <Chip
                                      size="small"
                                      label="Beta"
                                      sx={{ bgcolor: 'rgba(255,99,222,0.25)', color: T.colors.text, borderRadius: T.radii.chip, height: 20 }}
                                    />
                                  </Stack>
                                ) : (
                                  <Stack direction="row" spacing={0.5} alignItems="center">
                                    <Box component="span">{item.label || (isDashboard ? 'Dashboard' : '')}</Box>
                                    {topStatus && (
                                      <Chip
                                        size="small"
                                        label={topStatus}
                                        sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: T.colors.subText, borderRadius: T.radii.chip, height: 20 }}
                                      />
                                    )}
                                  </Stack>
                                )
                              }
                              secondary={isDashboard && rewardsBadge ? 'Unclaimed rewards' : undefined}
                              primaryTypographyProps={{ fontWeight: 800, color: T.colors.text }}
                              secondaryTypographyProps={{ color: T.colors.subText, fontSize: 12 }}
                            />
                          )}
                        </ListItemButton>
                      </ListItem>
                    </Tooltip>

                    {!collapsed && hasSub && (
                      <Box sx={{ pl: 4, mb: 0.5 }}>
                        {item.label === 'Games' ? (
                          <>
                            {/* Featured: hot or new across all games (includes PvP) */}
                            {item.subItems.some((g) => g.hot || g.new) && (
                              <>
                                <ListSubheader
                                  disableSticky
                                  sx={{
                                    color: T.colors.subText,
                                    fontSize: 11,
                                    fontWeight: 800,
                                    textTransform: 'uppercase',
                                    letterSpacing: 0.8,
                                    px: 0,
                                    mb: 0.25,
                                    position: 'sticky',
                                    top: 0,
                                    zIndex: 1,
                                    bgcolor: T.colors.background,
                                    borderBottom: `1px solid ${T.colors.border}`,
                                    pb: 0.25,
                                  }}
                                >
                                  Featured
                                </ListSubheader>
                                {item.subItems
                                  .filter((g) => g.hot || g.new)
                                  .map((s) => {
                                    const disabled = !!s.disabled;
                                    const status = disabled ? (s.soon ? 'Soon' : 'Dev') : (s.new ? 'New' : (s.hot ? 'Hot' : null));
                                    return (
                                      <ListItem key={`side-sub-${item.label}-${s.label}-featured`} disablePadding>
                                        <ListItemButton
                                          component={disabled ? 'div' : Link}
                                          to={disabled ? undefined : s.to}
                                          disabled={disabled}
                                          selected={isActive(s.to)}
                                          aria-current={isActive(s.to) ? 'page' : undefined}
                                          onClick={(e) => { if (disabled) { e.preventDefault?.(); toast.info(s.soon ? 'Coming soon' : 'Under development'); } }}
                                          sx={{
                                            borderRadius: T.radii.card,
                                            py: 0.4,
                                            pr: 1,
                                            minHeight: 32,
                                            transition: 'background-color .14s ease, border-color .14s ease',
                                            '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                                            '&:focus-visible': { outline: '2px solid rgba(124,77,255,0.45)', outlineOffset: 2 },
                                            '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.08)', borderLeft: `3px solid ${T.colors.primary}` },
                                          }}
                                        >
                                          <ListItemText
                                            primary={
                                              <Stack direction="row" spacing={0.5} alignItems="center">
                                                <Box component="span">{s.label}</Box>
                                                {s.group === 'pvp' && (
                                                  <Chip size="small" label="PvP" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: T.colors.text, borderRadius: T.radii.chip, height: 20 }} />
                                                )}
                                                {status && (
                                                  <Chip
                                                    size="small"
                                                    label={status}
                                                    sx={{
                                                      bgcolor: status === 'Hot' ? 'rgba(124,77,255,0.18)' : status === 'New' ? 'rgba(255,99,222,0.25)' : 'rgba(255,255,255,0.12)',
                                                      color: disabled ? T.colors.subText : T.colors.text,
                                                      borderRadius: T.radii.chip,
                                                      height: 20,
                                                    }}
                                                  />
                                                )}
                                              </Stack>
                                            }
                                            primaryTypographyProps={{ color: T.colors.subText, fontSize: 13 }}
                                          />
                                        </ListItemButton>
                                      </ListItem>
                                    );
                                  })}
                                <Box sx={{ height: 6 }} />
                              </>
                            )}

                            {/* All Games (A–Z), Blackjack pinned last */}
                            <ListSubheader
                              disableSticky
                              sx={{
                                color: T.colors.subText,
                                fontSize: 11,
                                fontWeight: 800,
                                textTransform: 'uppercase',
                                letterSpacing: 0.8,
                                px: 0,
                                mb: 0.25,
                                position: 'sticky',
                                top: 0,
                                zIndex: 1,
                                bgcolor: T.colors.background,
                                borderBottom: `1px solid ${T.colors.border}`,
                                pb: 0.25,
                              }}
                            >
                              All Games (A–Z)
                            </ListSubheader>
                            {item.subItems
                              .slice()
                              .sort((a, b) => {
                                const na = String(a.label || '').toLowerCase();
                                const nb = String(b.label || '').toLowerCase();
                                if (na === 'blackjack') return 1;
                                if (nb === 'blackjack') return -1;
                                return na.localeCompare(nb);
                              })
                              .map((s) => {
                                const disabled = !!s.disabled;
                                const status = disabled ? (s.soon ? 'Soon' : 'Dev') : (s.new ? 'New' : (s.hot ? 'Hot' : null));
                                return (
                                  <ListItem key={`side-sub-${item.label}-${s.label}-all`} disablePadding>
                                    <ListItemButton
                                      component={disabled ? 'div' : Link}
                                      to={disabled ? undefined : s.to}
                                      disabled={disabled}
                                      selected={isActive(s.to)}
                                      aria-current={isActive(s.to) ? 'page' : undefined}
                                      onClick={(e) => { if (disabled) { e.preventDefault?.(); toast.info(s.soon ? 'Coming soon' : 'Under development'); } }}
                                      sx={{
                                        borderRadius: T.radii.card,
                                        py: 0.4,
                                        pr: 1,
                                        minHeight: 32,
                                        transition: 'background-color .14s ease, border-color .14s ease',
                                        '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                                        '&:focus-visible': { outline: '2px solid rgba(124,77,255,0.45)', outlineOffset: 2 },
                                        '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.08)', borderLeft: `3px solid ${T.colors.primary}` },
                                      }}
                                    >
                                      <ListItemText
                                        primary={
                                          <Stack direction="row" spacing={0.5} alignItems="center">
                                            <Box component="span">{s.label}</Box>
                                            {s.group === 'pvp' && (
                                              <Chip size="small" label="PvP" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: T.colors.text, borderRadius: T.radii.chip, height: 20 }} />
                                            )}
                                            {status && (
                                              <Chip
                                                size="small"
                                                label={status}
                                                sx={{
                                                  bgcolor: status === 'Hot' ? 'rgba(124,77,255,0.18)' : status === 'New' ? 'rgba(255,99,222,0.25)' : 'rgba(255,255,255,0.12)',
                                                  color: disabled ? T.colors.subText : T.colors.text,
                                                  borderRadius: T.radii.chip,
                                                  height: 20,
                                                }}
                                              />
                                            )}
                                          </Stack>
                                        }
                                        primaryTypographyProps={{ color: T.colors.subText, fontSize: 13 }}
                                      />
                                    </ListItemButton>
                                  </ListItem>
                                );
                              })}
                          </>
                        ) : (
                          // Non-Games: render as a flat list with single status chip
                          <>
                            {item.subItems.map((s) => {
                              const disabled = !!s.disabled;
                              const status = disabled ? (s.soon ? 'Soon' : 'Dev') : (s.new ? 'New' : (s.hot ? 'Hot' : null));
                              return (
                                <ListItem key={`side-sub-${item.label}-${s.label}`} disablePadding>
                                  <ListItemButton
                                    component={disabled ? 'div' : Link}
                                    to={disabled ? undefined : s.to}
                                    disabled={disabled}
                                    selected={isActive(s.to)}
                                    aria-current={isActive(s.to) ? 'page' : undefined}
                                    onClick={(e) => { if (disabled) { e.preventDefault?.(); toast.info(s.soon ? 'Coming soon' : 'Under development'); } }}
                                    sx={{
                                      borderRadius: T.radii.card,
                                      py: 0.4,
                                      pr: 1,
                                      minHeight: 32,
                                      transition: 'background-color .14s ease, border-color .14s ease',
                                      '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                                      '&:focus-visible': { outline: '2px solid rgba(124,77,255,0.45)', outlineOffset: 2 },
                                      '&.Mui-selected': { bgcolor: 'rgba(255,255,255,0.08)', borderLeft: `3px solid ${T.colors.primary}` },
                                    }}
                                  >
                                    <ListItemText
                                      primary={
                                        <Stack direction="row" spacing={0.5} alignItems="center">
                                          <Box component="span">{s.label}</Box>
                                          {status && (
                                            <Chip
                                              size="small"
                                              label={status}
                                              sx={{
                                                bgcolor: status === 'Hot' ? 'rgba(124,77,255,0.18)' : status === 'New' ? 'rgba(255,99,222,0.25)' : 'rgba(255,255,255,0.12)',
                                                color: disabled ? T.colors.subText : T.colors.text,
                                                borderRadius: T.radii.chip,
                                                height: 20,
                                              }}
                                            />
                                          )}
                                        </Stack>
                                      }
                                      primaryTypographyProps={{ color: T.colors.subText, fontSize: 13 }}
                                    />
                                  </ListItemButton>
                                </ListItem>
                              );
                            })}
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          ));
        })()}
      </List>

      {/* Community/social column */}
      <Box sx={{ px: collapsed ? 0 : 1.25, mb: 0.5 }}>
        {!collapsed && (
          <ListSubheader
            disableSticky
            sx={{
              color: T.colors.subText,
              fontSize: 11,
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: 0.8,
              px: 0,
              mb: 0.25,
              position: 'sticky',
              top: 0,
              zIndex: 1,
              bgcolor: T.colors.background,
              borderBottom: `1px solid ${T.colors.border}`,
              pb: 0.25,
            }}
          >
            Community
          </ListSubheader>
        )}

        <List sx={{ px: collapsed ? 0 : 0, py: 0 }}>
          <ListItem disablePadding>
            <Tooltip title={collapsed ? 'Twitter' : ''} placement="right">
              <ListItemButton
                component="a"
                href={process.env.REACT_APP_X_URL || process.env.REACT_APP_TWITTER_URL || 'https://x.com/chainplaydotbet'}
                target="_blank"
                rel="noopener noreferrer nofollow"
                aria-label={collapsed ? 'Twitter' : undefined}
                sx={{
                  borderRadius: T.radii.card,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  px: collapsed ? 1 : 1.25,
                  minHeight: 40,
                  '&:hover': { bgcolor: 'rgba(29,161,242,0.12)' },
                }}
              >
                <ListItemIcon sx={{ color: '#1DA1F2', minWidth: collapsed ? 0 : 36, mr: collapsed ? 0 : 1, '& svg': { fontSize: 22 } }}>
                  <TwitterIcon />
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary="Twitter"
                    primaryTypographyProps={{ fontWeight: 800, color: T.colors.text }}
                    secondary="Follow updates"
                    secondaryTypographyProps={{ color: T.colors.subText, fontSize: 12 }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          <ListItem disablePadding>
            <Tooltip title={collapsed ? 'Telegram' : ''} placement="right">
              <ListItemButton
                component="a"
                href={process.env.REACT_APP_TELEGRAM_URL || 'https://t.me/'}
                target="_blank"
                rel="noopener noreferrer nofollow"
                aria-label={collapsed ? 'Telegram' : undefined}
                sx={{
                  borderRadius: T.radii.card,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  px: collapsed ? 1 : 1.25,
                  minHeight: 40,
                  '&:hover': { bgcolor: 'rgba(42,171,238,0.12)' },
                }}
              >
                <ListItemIcon sx={{ color: '#2AABEE', minWidth: collapsed ? 0 : 36, mr: collapsed ? 0 : 1, '& svg': { fontSize: 22 } }}>
                  <TelegramIcon />
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary="Telegram"
                    primaryTypographyProps={{ fontWeight: 800, color: T.colors.text }}
                    secondary="Chat with community"
                    secondaryTypographyProps={{ color: T.colors.subText, fontSize: 12 }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>

          <ListItem disablePadding>
            <Tooltip title={collapsed ? 'GitHub' : ''} placement="right">
              <ListItemButton
                component="a"
                href={process.env.REACT_APP_GITHUB_URL || 'https://github.com/'}
                target="_blank"
                rel="noopener noreferrer nofollow"
                aria-label={collapsed ? 'GitHub' : undefined}
                sx={{
                  borderRadius: T.radii.card,
                  justifyContent: collapsed ? 'center' : 'flex-start',
                  px: collapsed ? 1 : 1.25,
                  minHeight: 40,
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.08)' },
                }}
              >
                <ListItemIcon sx={{ color: T.colors.text, minWidth: collapsed ? 0 : 36, mr: collapsed ? 0 : 1, '& svg': { fontSize: 22 } }}>
                  <GitHubIcon />
                </ListItemIcon>
                {!collapsed && (
                  <ListItemText
                    primary="GitHub"
                    primaryTypographyProps={{ fontWeight: 800, color: T.colors.text }}
                    secondary="View code"
                    secondaryTypographyProps={{ color: T.colors.subText, fontSize: 12 }}
                  />
                )}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        </List>
      </Box>

      <Divider sx={{ my: 0.5, opacity: 0.18 }} />

      {/* Footer: compact status */}
      <Box sx={{ p: 1.25 }}>
        {/* Intentionally minimal: Community links are above; no jackpot/VIP or extra socials here */}
      </Box>
    </Box>
  );

  return (
    <>
      {!isMobile && (
        <Drawer
          variant="permanent"
          anchor="left"
          PaperProps={{
            sx: {
              position: 'fixed',
              top: 'var(--topbar-height, 56px)',
              height: 'calc(100% - var(--topbar-height, 56px))',
              width: `${collapsed ? COLLAPSED_WIDTH : NAV_WIDTH}px`,
              bgcolor: T.colors.background,
              borderRight: `1px solid ${T.colors.border}`,
              overflow: 'hidden',
              transition: 'width .18s ease',
            }
          }}
        >
          <Box role="navigation" aria-label="Primary" sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
            {/* Wrap the existing sidebar content in a scrollable container */}
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                overflowY: 'auto',
                scrollbarWidth: 'thin',
                '&::-webkit-scrollbar': { width: 6 },
                '&::-webkit-scrollbar-thumb': { backgroundColor: 'rgba(255,255,255,0.18)', borderRadius: 8 },
                '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
              }}
              ref={sideScrollRef}
              onKeyDown={sideKeyHandler}
            >
              {SideBarContent}
            </Box>
          </Box>
        </Drawer>
      )}
      <AppBar
        position="fixed"
        role="navigation"
        sx={{
          bgcolor: elevated ? `${'var(--color-background)'}F2` : `${'var(--color-background)'}CC`,
          backdropFilter: 'saturate(140%) blur(8px)',
          borderBottom: `1px solid ${T.colors.border}`,
          boxShadow: elevated ? '0 6px 24px rgba(0,0,0,0.25)' : 'none',
          transition: 'background-color .18s ease, box-shadow .18s ease, border-color .18s ease',
          zIndex: T.z.bar + 1,
          height: 'var(--topbar-height, 56px)',
        }}
      >
        <Toolbar
          sx={{
            minHeight: 'var(--topbar-height, 56px)',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: { xs: 1, md: 1.5 },
            pl: { xs: 1.5, sm: 2, md: 2.5 },
            pr: { xs: 2, sm: 2.5, md: 3.5 },
            py: 0,
            width: '100%',
            position: 'relative',
          }}
        >
          {/* Left group: hamburger hard-left + divider + logo with comfortable separation */}
          <Stack direction="row" spacing={1.25} alignItems="center">
            <Tooltip title={isMobile ? (drawerOpen ? 'Close menu' : 'Open menu') : (collapsed ? 'Expand menu' : 'Collapse menu')}>
              <IconButton
                onClick={() => { if (isMobile) { toggleDrawer(); } else { setCollapsed((c) => !c); } }}
                sx={{
                  color: T.colors.text,
                  border: `1px solid ${T.colors.border}`,
                  borderRadius: 1.5,
                  width: { xs: 40, md: 36 },
                  height: { xs: 40, md: 36 },
                  transition: 'transform 120ms ease, border-color 120ms ease',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                  '&:active': { transform: 'scale(0.98)' },
                  '&:focus-visible': { outline: '2px solid rgba(124,77,255,0.55)', outlineOffset: 2 },
                }}
                aria-label={isMobile ? (drawerOpen ? 'Close menu' : 'Open menu') : (collapsed ? 'Expand sidebar' : 'Collapse sidebar')}
                aria-controls={isMobile ? 'main-nav-drawer' : undefined}
                aria-expanded={isMobile ? (drawerOpen ? true : undefined) : (!collapsed ? true : undefined)}
                aria-pressed={!isMobile ? (!collapsed ? true : undefined) : undefined}
              >
                <motion.span
                  animate={{ rotate: isMobile ? (drawerOpen ? 90 : 0) : (!collapsed ? 90 : 0) }}
                  transition={{ duration: 0.18, ease: 'easeOut' }}
                  style={{ display: 'inline-flex' }}
                >
                  {isMobile
                    ? (drawerOpen ? <CloseRoundedIcon fontSize="small" /> : <MenuIcon fontSize="small" />)
                    : (!collapsed ? <MenuOpenIcon fontSize="small" /> : <MenuIcon fontSize="small" />)}
                </motion.span>
              </IconButton>
            </Tooltip>
            <Divider orientation="vertical" flexItem sx={{ height: 28, opacity: 0.22, mx: 0.5 }} />
            <Box sx={{ display: 'flex', alignItems: 'center', ml: 0.5, px: 0.5, minHeight: 36 }}>
              <Link to="/" aria-label="Home" style={{ display: 'inline-flex' }}>
                <img
                  src={logoSrc}
                  onError={onLogoError}
                  alt="ChainPlay logo"
                  height={isMobile ? 24 : 28}
                  width={isMobile ? 124 : 148}
                  style={{ display: 'block', objectFit: 'contain' }}
                  loading="eager"
                  decoding="async"
                />
              </Link>
            </Box>
            {/* Active network chip (desktop) */}
            <Box sx={{ display: { xs: 'none', md: 'inline-flex' } }}>
              <Chip
                icon={<PublicIcon sx={{ fontSize: 16 }} />}
                label={activeNetworkLabel}
                onClick={openChain}
                aria-haspopup="menu"
                aria-controls="network-menu"
                sx={{
                  height: 28,
                  borderRadius: T.radii.chip,
                  color: T.colors.text,
                  border: `1px solid ${wrongNetwork ? T.colors.warning : T.colors.border}`,
                  bgcolor: wrongNetwork ? 'rgba(255,193,7,0.12)' : 'rgba(255,255,255,0.06)',
                  fontWeight: 800,
                  letterSpacing: 0.3,
                  '& .MuiChip-icon': { color: wrongNetwork ? T.colors.warning : T.colors.accent, ml: 0.25 },
                  '&:hover': { bgcolor: wrongNetwork ? 'rgba(255,193,7,0.18)' : 'rgba(255,255,255,0.09)' },
                  cursor: 'pointer',
                }}
              />
            </Box>
          </Stack>

          {/* Center element removed */}

          {/* Right controls */}
          <Stack
            direction="row"
            spacing={{ xs: 1, sm: 1.25, md: 1.25 }}
            alignItems="center"
            sx={{
              '& .MuiIconButton-root': {
                width: 36,
                height: 36,
                borderRadius: 1.5,
                color: T.colors.text,
                transition: 'background-color .14s ease, transform .12s ease',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' },
                '&:focus-visible': { outline: '2px solid rgba(124,77,255,0.45)', outlineOffset: 2 },
                '&:active': { transform: 'scale(0.98)' },
              },
            }}
          >
            {/* Desktop cluster: Jackpot • VIP • Balances */}
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ display: { xs: 'none', md: 'flex' } }}
            >
              <JackpotMini value={jackpot} />
              <VIPBadge totalCPT={totalCPT} />
              <Tooltip title="ETH and CPT balances">
                {isWalletLoading ? (
                  <Skeleton variant="rounded" width={160} height={28} sx={{ borderRadius: T.radii.chip }} />
                ) : walletError ? (
                  <Typography sx={{ color: T.colors.danger, fontWeight: 800 }}>Wallet Error</Typography>
                ) : (
                  <motion.div key={debouncedBalanceKey} initial={{ opacity: 0.6 }} animate={{ opacity: 1 }} transition={{ duration: 0.4 }}>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{
                        px: 1,
                        height: 28,
                        borderRadius: T.radii.chip,
                        bgcolor: 'rgba(255,255,255,0.06)',
                        border: `1px solid ${T.colors.border}`,
                        color: T.colors.text,
                        fontWeight: 900,
                        fontSize: 12,
                        letterSpacing: 0.4,
                        whiteSpace: 'nowrap',
                        fontVariantNumeric: 'tabular-nums',
                        transition: 'box-shadow .18s ease, background-color .18s ease',
                        boxShadow: '0 0 0 0 rgba(0,0,0,0)',
                        '&:hover': {
                          bgcolor: 'rgba(255,255,255,0.09)',
                          boxShadow: '0 0 0 1px rgba(124,77,255,0.45) inset, 0 6px 18px rgba(124,77,255,0.25)',
                        },
                      }}
                    >
                      <Box component="span">ETH {fmt(safeBalance.eth, 4)}</Box>
                      <Box component="span" sx={{ opacity: 0.6 }}>•</Box>
                      <Box component="span">CPT {fmt(safeBalance.cpt, 2)}</Box>
                    </Stack>
                  </motion.div>
                )}
              </Tooltip>
            </Stack>

            {/* Divider between desktop cluster and wallet */}
            {!isMobile && (
              <Divider orientation="vertical" flexItem sx={{ mx: 0.75, opacity: 0.22, borderColor: T.colors.border }} />
            )}

            {/* Wallet (Connect) */}
            <Tooltip title={address ? 'Open wallet menu' : 'Connect wallet'}>
              <Box sx={{ height: 36, display: 'flex', alignItems: 'center' }}>
                <WalletButton />
              </Box>
            </Tooltip>

            {/* Divider between wallet and profile */}
            {!isMobile && (
              <Divider orientation="vertical" flexItem sx={{ mx: 0.75, opacity: 0.22, borderColor: T.colors.border }} />
            )}

            {/* Profile */}
            <Tooltip title={address ? shortAddr(address) : 'Guest'}>
              <Box sx={{ height: 36, display: 'flex', alignItems: 'center' }}>
                <VIPAvatar
                  address={address}
                  progress={Math.max(0, Math.min(100, totalCPT >= vipLevels[1].need ? ((totalCPT - vipLevels[0].need) / (vipLevels[1].need - vipLevels[0].need)) * 100 : totalCPT > 0 ? 5 : 0))}
                  color={vipLevels.find((v) => totalCPT >= v.need)?.color || theme.vip.palette.shrimp}
                  hasRewards={rewardsBadge}
                  onClick={(e) => { if (campLimited) { campJoinToast(); return; } setAnchorProfile(e.currentTarget); }}
                />
              </Box>
            </Tooltip>

            {/* Divider between profile and chat */}
            {!isMobile && (
              <Divider orientation="vertical" flexItem sx={{ mx: 0.75, opacity: 0.22, borderColor: T.colors.border }} />
            )}

            {/* UI Settings (header button) */}
            {!isMobile && (
              <Tooltip title="UI Settings (Ctrl/Cmd+,)">
                <IconButton
                  onClick={() => { try { window.dispatchEvent(new Event('cp:ui:settings:open')); } catch {} }}
                  sx={{ color: T.colors.text, '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' } }}
                  aria-label="Open UI settings"
                >
                  <SettingsRoundedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* Chat toggle (rightmost on desktop) */}
            {!isMobile && (
              <Tooltip title="Toggle chat (Ctrl/Cmd+J)">
                <IconButton
                  onClick={() => { try { window.dispatchEvent(new Event('toggle_chat_dock')); } catch {} }}
                  sx={{ color: T.colors.text, '&:hover': { bgcolor: 'rgba(255,255,255,0.06)' } }}
                  aria-label="Toggle chat"
                >
                  <ChatBubbleOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Toolbar>

        <Menu
          anchorEl={anchorProfile}
          open={Boolean(anchorProfile)}
          onClose={() => setAnchorProfile(null)}
          PaperProps={{ sx: { bgcolor: T.colors.card, borderRadius: T.radii.card, border: `1px solid ${T.colors.border}`, minWidth: 300 } }}
        >
          <MenuItem disabled sx={{ color: T.colors.subText, fontWeight: 700 }}>
            {address ? shortAddr(address) : 'Guest'}
          </MenuItem>
          <MenuItem component={Link} to="/dashboard" onClick={() => setAnchorProfile(null)} sx={{ color: T.colors.text, '&:hover': { bgcolor: T.colors.surfaceAlt } }}>
            <ListItemIcon sx={{ color: T.colors.text, minWidth: 32 }}><AccountCircleIcon fontSize="small" /></ListItemIcon>
            Dashboard
          </MenuItem>
          <Divider />
          {address && (
            <MenuItem onClick={copyAddress} sx={{ color: T.colors.text, '&:hover': { bgcolor: T.colors.surfaceAlt } }}>
              <ListItemIcon sx={{ color: T.colors.text, minWidth: 32 }}><ContentCopyIcon fontSize="small" /></ListItemIcon>
              Copy Address
            </MenuItem>
          )}
          {address && (
            <MenuItem onClick={copyReferral} sx={{ color: T.colors.text, '&:hover': { bgcolor: T.colors.surfaceAlt } }}>
              <ListItemIcon sx={{ color: T.colors.text, minWidth: 32 }}><ShareIcon fontSize="small" /></ListItemIcon>
              Copy Referral
            </MenuItem>
          )}
          <MenuItem component={Link} to="/promotions" onClick={() => setAnchorProfile(null)} sx={{ color: T.colors.text, '&:hover': { bgcolor: T.colors.surfaceAlt } }}>
            <ListItemIcon sx={{ color: rewardsBadge ? T.colors.primary : T.colors.text, minWidth: 32 }}>
              <RocketLaunchIcon fontSize="small" />
            </ListItemIcon>
            {rewardsBadge ? 'Unclaimed Rewards' : 'Promotions'}
          </MenuItem>
          <MenuItem component={Link} to="/staking" onClick={() => setAnchorProfile(null)} sx={{ color: T.colors.text, '&:hover': { bgcolor: T.colors.surfaceAlt } }}>
            <ListItemIcon sx={{ color: T.colors.text, minWidth: 32 }}><ShieldOutlinedIcon fontSize="small" /></ListItemIcon>
            Security & Limits
          </MenuItem>
          <MenuItem
            onClick={() => { try { window.dispatchEvent(new Event('cp:ui:settings:open')); } catch {} setAnchorProfile(null); }}
            sx={{ color: T.colors.text, '&:hover': { bgcolor: T.colors.surfaceAlt } }}
          >
            <ListItemIcon sx={{ color: T.colors.text, minWidth: 32 }}><SettingsRoundedIcon fontSize="small" /></ListItemIcon>
            Interface Settings
          </MenuItem>
          {address && <Divider />}
          {address && (
            <MenuItem onClick={() => setAnchorProfile(null)} sx={{ color: T.colors.text, '&:hover': { bgcolor: T.colors.surfaceAlt } }}>
              <ListItemIcon sx={{ color: T.colors.text, minWidth: 32 }}><LogoutIcon fontSize="small" /></ListItemIcon>
              Logout
            </MenuItem>
          )}
        </Menu>

        <Menu
          id="network-menu"
          anchorEl={anchorChain}
          open={Boolean(anchorChain)}
          onClose={closeChain}
          PaperProps={{
            sx: {
              bgcolor: T.colors.card,
              backgroundImage: 'none',
              borderRadius: T.radii.card,
              border: `1px solid ${T.colors.border}`,
              minWidth: 240,
              p: 0.5,
              boxShadow: '0 16px 38px rgba(0,0,0,0.55)',
              opacity: 1,
              backdropFilter: 'none',
              filter: 'none',
            }
          }}
        >
          <MenuItem disabled sx={{ opacity: 0.7, fontWeight: 800, color: T.colors.subText }}>
            Select Network
          </MenuItem>
          {chains.map((c) => {
            const disabled = !!c.comingSoon && c.key !== 'camp';
            const isCurrent = ((ctxChainId || 8453) === c.chainId);
            return (
              <MenuItem
                key={c.key}
                disabled={disabled}
                onClick={() => !disabled && doSwitchChain(c.key)}
                sx={{
                  color: T.colors.text,
                  opacity: disabled ? 0.55 : 1,
                  borderRadius: T.radii.card,
                  '&:hover': { bgcolor: T.colors.surfaceAlt, color: disabled ? T.colors.subText : T.colors.accent },
                  display: 'flex', justifyContent: 'space-between',
                }}
              >
                <span>{c.name}</span>
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {isCurrent && <CheckRoundedIcon sx={{ fontSize: 16, color: T.colors.primary }} />}
                  {disabled && <Chip size="small" label="Soon" sx={{ bgcolor: 'rgba(255,255,255,0.12)', color: T.colors.subText, borderRadius: T.radii.chip }} />}
                </Stack>
              </MenuItem>
            );
          })}
        </Menu>

        <Drawer
          id="main-nav-drawer"
          anchor="right"
          open={drawerOpen}
          onClose={closeDrawer}
          PaperProps={{
            sx: {
              bgcolor: T.colors.background,
              borderLeft: `1px solid ${T.colors.border}`,
              width: { xs: '88vw', sm: 340 },
              maxWidth: 420,
            }
          }}
          ModalProps={{ keepMounted: true }}
          slotProps={{ backdrop: { 'aria-hidden': true } }}
        >
          {DrawerContent}
        </Drawer>
      </AppBar>
    </>
  );
};

export default Navbar;
